customElements.whenDefined('dialog-component').then(() => {
  const DialogComponent = customElements.get('dialog-component');

  class RegistrationModal extends DialogComponent {
    /** @type {Element | null} */
    #triggerElement = null;

    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('dialog:close', this.#onClose);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('dialog:close', this.#onClose);
      this.#form?.removeEventListener('submit', this.#onSubmit);
      this.#removeBlurListeners();
    }

    get #submitBtn() {
      return /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.registration-modal__submit')
      );
    }

    get #form() {
      return /** @type {HTMLFormElement | null} */ (
        this.querySelector('.registration-modal__form')
      );
    }

    get #firstField() {
      return /** @type {HTMLElement | null} */ (
        this.querySelector('.registration-modal__field input')
      );
    }

    get #successPanel() {
      return /** @type {HTMLElement | null} */ (
        this.querySelector('.registration-modal__success')
      );
    }

    get #formWrapper() {
      return /** @type {HTMLElement | null} */ (
        this.querySelector('.registration-modal__form-wrapper')
      );
    }

    get #syncEndpoint() {
      return this.dataset.syncEndpoint?.trim() || '';
    }

    showDialog() {
      this.#triggerElement = document.activeElement;
      this.#resetState();
      this.#form?.addEventListener('submit', this.#onSubmit);
      this.#addBlurListeners();
      this.#addInputListeners();
      super.showDialog();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.#firstField?.focus())
      );
    }

    #onClose = () => {
      if (this.#triggerElement instanceof HTMLElement) {
        this.#triggerElement.focus();
      }
      this.#triggerElement = null;
      this.#form?.removeEventListener('submit', this.#onSubmit);
      this.#removeBlurListeners();
      this.#removeInputListeners();
    };

    #addInputListeners() {
      for (const field of this.querySelectorAll('[required]')) {
        field.addEventListener('input', this.#checkFormValidity);
        field.addEventListener('change', this.#checkFormValidity);
      }
    }

    #removeInputListeners() {
      for (const field of this.querySelectorAll('[required]')) {
        field.removeEventListener('input', this.#checkFormValidity);
        field.removeEventListener('change', this.#checkFormValidity);
      }
    }

    #checkFormValidity = () => {
      const form = this.#form;
      const btn = this.#submitBtn;
      if (!form || !btn) return;
      btn.disabled = !form.checkValidity();
    };

    #addBlurListeners() {
      for (const field of this.querySelectorAll('[required]')) {
        // DOB se valida como grupo en submit; no spamear errores en blur individual
        if (/** @type {HTMLInputElement} */ (field).name === 'inp_4[]') continue;
        field.addEventListener('blur', this.#onFieldBlur);
      }
    }

    #removeBlurListeners() {
      for (const field of this.querySelectorAll('[required]')) {
        if (/** @type {HTMLInputElement} */ (field).name === 'inp_4[]') continue;
        field.removeEventListener('blur', this.#onFieldBlur);
      }
    }

    /** @param {Event} event */
    #onFieldBlur = (event) => {
      const field = /** @type {HTMLInputElement} */ (event.currentTarget);
      const wrapper = field.closest(
        '.registration-modal__field, .registration-modal__legal'
      );
      this.#clearError(field, wrapper);
      if (!field.checkValidity()) {
        this.#showError(field, wrapper);
      }
    };

    /** @param {SubmitEvent} event */
    #onSubmit = async (event) => {
      event.preventDefault();
      if (!this.#validateForm()) return;

      const form = this.#form;
      if (!form) return;

      const btn = /** @type {HTMLButtonElement | null} */ (
        this.querySelector('.registration-modal__submit')
      );
      if (btn) btn.setAttribute('aria-busy', 'true');

      const iframe = /** @type {HTMLIFrameElement | null} */ (
        this.querySelector('iframe[data-registration-target]')
      );

      try {
        // El form tiene target="registration-iframe-{id}" → la respuesta de Emarsys
        // cae en el iframe oculto y NO navega la página. Esperamos su `load` event
        // (o un timeout de 6s como fallback) para confirmar que se envió.
        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve(undefined);
          };
          if (iframe) iframe.addEventListener('load', finish, { once: true });
          setTimeout(finish, 6000);
          form.submit();
        });

        const payload = this.#buildSyncPayload(form);

        this.#showSuccess();
        this.#dispatchRegistrationSuccess(payload);
        this.#sendSyncPayload(payload);
      } finally {
        if (btn) btn.removeAttribute('aria-busy');
      }
    };

    /**
     * Serializa los campos del form al schema crudo de Emarsys (inp_1, inp_2, ...).
     * Los campos repetidos como inp_4[] (día/mes/año) se acumulan como array.
     * @param {HTMLFormElement} form
     */
    #buildSyncPayload(form) {
      const formData = new FormData(form);
      /** @type {Record<string, string | string[]>} */
      const fields = {};

      for (const [key, value] of formData.entries()) {
        const v = typeof value === 'string' ? value.trim() : '';
        if (Object.prototype.hasOwnProperty.call(fields, key)) {
          const existing = fields[key];
          if (Array.isArray(existing)) {
            existing.push(v);
          } else {
            fields[key] = [existing, v];
          }
        } else {
          fields[key] = v;
        }
      }

      return {
        fields,
        meta: {
          source: 'registration-modal',
          submitted_at: new Date().toISOString(),
          shopify_form: 'emarsys',
        },
      };
    }

    /** @param {{ customer: object, extra_fields: object, meta: object }} payload */
    #dispatchRegistrationSuccess(payload) {
      this.dispatchEvent(new CustomEvent('registration-modal:registration-success', {
        detail: payload,
        bubbles: true,
        composed: true,
      }));
    }

    /** @param {{ customer: object, extra_fields: object, meta: object }} payload */
    #sendSyncPayload(payload) {
      const endpoint = this.#syncEndpoint;
      if (!endpoint) return;

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((error) => {
        console.warn('Registration modal sync failed', error);
      });
    }

    /** @returns {boolean} */
    #validateForm() {
      const form = this.#form;
      if (!form) return true;

      /** @type {HTMLElement | null} */
      let firstInvalid = null;

      // 1) Validar el grupo DOB primero (los 3 controles juntos)
      const dobInvalid = this.#validateDOB();
      if (dobInvalid instanceof HTMLElement) {
        firstInvalid = dobInvalid;
      }

      // 2) Validar el resto de campos required (saltando los DOB ya manejados)
      for (const field of form.querySelectorAll('[required]')) {
        if (/** @type {HTMLInputElement} */ (field).name === 'inp_4[]') continue;

        const wrapper = field.closest(
          '.registration-modal__field, .registration-modal__legal'
        );
        this.#clearError(field, wrapper);

        if (!/** @type {HTMLInputElement} */ (field).checkValidity()) {
          this.#showError(field, wrapper);
          if (firstInvalid === null) firstInvalid = /** @type {HTMLElement} */ (field);
        }
      }

      if (firstInvalid !== null) firstInvalid.focus();
      return firstInvalid === null;
    }

    /**
     * Valida los 3 campos de fecha de nacimiento como un grupo único.
     * Si falta cualquiera de los 3, muestra un único mensaje y retorna el primer input vacío.
     * @returns {HTMLElement | null}
     */
    #validateDOB() {
      const form = this.#form;
      if (!form) return null;

      const wrapper = /** @type {HTMLElement | null} */ (
        form.querySelector('[data-dob-group]')
      );
      if (!wrapper) return null;

      const inputs = /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement>} */ (
        form.querySelectorAll('[name="inp_4[]"]')
      );

      // Limpiar estado previo
      const existingError = wrapper.querySelector('.registration-modal__error');
      if (existingError) existingError.remove();
      for (const input of inputs) {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }

      /** @type {HTMLElement | null} */
      let firstEmpty = null;
      for (const input of inputs) {
        if (!input.value.trim()) {
          input.setAttribute('aria-invalid', 'true');
          if (firstEmpty === null) firstEmpty = /** @type {HTMLElement} */ (input);
        }
      }

      if (firstEmpty === null) return null;

      const errorId = 'registration-dob-error';
      const msg = document.createElement('span');
      msg.id = errorId;
      msg.setAttribute('role', 'alert');
      msg.className = 'registration-modal__error';
      msg.textContent = 'Completá día, mes y año';
      wrapper.appendChild(msg);

      for (const input of inputs) {
        if (input.getAttribute('aria-invalid') === 'true') {
          input.setAttribute('aria-describedby', errorId);
        }
      }

      return firstEmpty;
    }

    /**
     * @param {Element} field
     * @param {Element | null} wrapper
     */
    #showError(field, wrapper) {
      const errorId = field.id + '-error';
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', errorId);

      let msg = wrapper ? wrapper.querySelector('#' + errorId) : null;
      if (!msg) {
        msg = document.createElement('span');
        msg.id = errorId;
        msg.setAttribute('role', 'alert');
        msg.className = 'registration-modal__error';
        if (wrapper) wrapper.appendChild(msg);
      }

      msg.textContent = /** @type {HTMLInputElement} */ (field).validationMessage;
    }

    /**
     * @param {Element} field
     * @param {Element | null} wrapper
     */
    #clearError(field, wrapper) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      if (wrapper) {
        const msg = wrapper.querySelector('.registration-modal__error');
        if (msg) msg.remove();
      }
    }

    #showSuccess() {
      const success = this.#successPanel;
      const formWrapper = this.#formWrapper;
      if (!success || !formWrapper) return;

      formWrapper.classList.add('is-hiding');
      formWrapper.addEventListener('transitionend', () => {
        formWrapper.hidden = true;
        formWrapper.classList.remove('is-hiding');
        success.hidden = false;
        /** @type {any} */ (this).classList.add('is-success');
        const focusTarget = success.querySelector('img, [tabindex]') || success;
        if (focusTarget instanceof HTMLElement) focusTarget.focus();
      }, { once: true });
    }

    #resetState() {
      const success = this.#successPanel;
      const formWrapper = this.#formWrapper;
      if (!success || !formWrapper) return;

      success.hidden = true;
      formWrapper.hidden = false;
      formWrapper.classList.remove('is-hiding');

      /** @type {any} */ (this).classList.remove('is-success');

      const form = this.#form;
      if (!form) return;
      form.reset();

      for (const field of form.querySelectorAll('[required]')) {
        const wrapper = field.closest(
          '.registration-modal__field, .registration-modal__legal'
        );
        this.#clearError(field, wrapper);
      }
    }
  }

  if (!customElements.get('registration-modal')) {
    customElements.define('registration-modal', RegistrationModal);
  }

  document.addEventListener('click', (event) => {
    if (!/** @type {Element} */ (event.target).closest('[data-open-newsletter-modal]')) return;
    const modal = document.querySelector('registration-modal');
    if (modal instanceof RegistrationModal) modal.showDialog();
  });
});
